// The ONE place "what options did the shopper pick" gets read for the
// whole embed. Phase 1 (15c92b56) wired APO v2 capture only into the
// auto-place button's own click-time payload builders
// (product-button.ts's buildOpenRequestOpts / buildAddToQuotePayload) —
// live forensics on majesticfountains.com showed their button is the
// theme app block (`qa-request-button`), which never runs that code at
// all, so nothing was captured. This module gives index.ts's item-
// seeding chokepoints (openRequest's single-item prefill,
// handleAddToQuote) a shared helper so app block, manual
// `QuoteApp.openRequest`/`QuoteApp.addToQuote` snippets, and auto-place
// all get covered from one place, regardless of which door the click
// came through.
//
// De-dupe: auto-place's own builders still do their own capture (scoped
// to the exact form the click resolved to — correct on multi-form pages
// in a way a page-wide `findProductForm` re-scan can't guarantee) and
// thread the result through opts.properties / payload.properties. The
// core chokepoints never re-capture on top of that — resolveSeedProperties
// below treats an already-non-empty properties array (auto-place's own
// capture, or a caller's genuinely explicit properties) as final and
// skips the page read entirely. A property is captured in exactly one
// place per click, whichever comes first.

import { readApoSelections } from "./apo-adapter";
import { findProductForm, mergeLineItemProperties, readLineItemProperties, type LineItemProperty } from "./product-button";

/** `readApoSelections(win, doc)` merged with `readLineItemProperties`
 *  off the nearest product form (found via the existing
 *  `findProductForm(doc)` — same form-scoring auto-place relies on),
 *  form inputs winning name collisions (phase-1 semantics, reused via
 *  `mergeLineItemProperties`). No product form on the page AND no APO
 *  v2 global → `[]`, same as today. Wrapped: any throw anywhere
 *  (hostile page, changed 3rd-party API shape) degrades to `[]` — this
 *  runs inside the click path on storefronts we don't control and must
 *  never break a quote open. */
export function captureProductOptions(win: any, doc: any): LineItemProperty[] {
  try {
    const form = findProductForm(doc);
    const formProps = form ? readLineItemProperties(form as any) : [];
    const apoProps = readApoSelections(win, doc);
    return mergeLineItemProperties(formProps, apoProps);
  } catch {
    return [];
  }
}

/** Decide what properties to seed onto a line item at one of the core
 *  chokepoints. `existing` wins untouched whenever it's a non-empty
 *  array — this covers BOTH a caller's genuinely explicit properties
 *  (e.g. a manual snippet passing its own `properties`) AND auto-place's
 *  own already-captured properties (product-button.ts), so neither gets
 *  overridden and neither gets captured twice. Only when `existing` is
 *  empty/absent AND a product/variant id is actually being seeded
 *  (`hasProductContext`) does this fall through to a fresh page read —
 *  a bare `openRequest()` mall-door open (no product context) never
 *  touches the DOM for this. */
export function resolveSeedProperties(opts: {
  hasProductContext: boolean;
  existing?: LineItemProperty[] | null;
  win: any;
  doc: any;
}): LineItemProperty[] {
  if (Array.isArray(opts.existing) && opts.existing.length) return opts.existing;
  if (!opts.hasProductContext) return [];
  try {
    return captureProductOptions(opts.win, opts.doc);
  } catch {
    return [];
  }
}
